import { useState } from 'react';

import { Button, ButtonVariation } from '../../../components/Button/Button';
import { DatePicker } from '../../../components/DatePicker';
import { RequiredFieldsLegend } from '../../../components/RequiredFieldsLegend';
import type { RosterEntry } from '../types';
import './RemoveUserModal.css';

interface RemoveUserModalProps {
  user: RosterEntry;
  onCancel: () => void;
  onConfirm: (userId: string, removalDate: string) => void;
}

function RemoveUserModal({ user, onCancel, onConfirm }: RemoveUserModalProps) {
  const baseTestId = 'remove-user-modal';
  // Normalize to start-of-day: the Calendar disables cells where `date < fromDate`, and a
  // day cell is built at midnight. Using `new Date()` (with the current time) as fromDate
  // would put today's midnight cell before it and disable TODAY — which is exactly the
  // date needed for an immediate removal (per the offboarding design, immediate = today).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setHours(0, 0, 0, 0);
  maxDate.setFullYear(maxDate.getFullYear() + 1);

  const [selectedDate, setSelectedDate] = useState<Date | null>(today);
  const userName = `${user.legalFirstName} ${user.legalLastName}`;

  const handleDateChange = (date: Date | null, formattedDate: string) => {
    setSelectedDate(date);
  };

  const handleConfirm = () => {
    if (selectedDate) {
      const formattedDate = selectedDate.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      onConfirm(user.userId, formattedDate);
    }
  };

  const isConfirmDisabled = !selectedDate;

  return (
    <div
      className='remove-user-modal'
      data-testid={baseTestId}>
      <p
        className='remove-user-modal__text'
        data-testid={`${baseTestId}-text`}>
        Select the date you want <strong data-testid={`${baseTestId}-user-name`}>{userName}</strong> to be removed and
        inactive from this contract.
      </p>
      <RequiredFieldsLegend data-testid={`${baseTestId}-required-fields`} />

      <div
        className='remove-user-modal__date-picker-container'
        data-testid={`${baseTestId}-date`}>
        <DatePicker
          required
          data-testid={`${baseTestId}-date-picker`}
          defaultValue={today}
          fromDate={today}
          label='Removal Date'
          name='removal-date'
          placeholder='MM/DD/YYYY'
          toDate={maxDate}
          onDateChange={handleDateChange}
        />
      </div>

      <div
        className='remove-user-modal__actions'
        data-testid={`${baseTestId}-actions`}>
        <Button
          isAutoWidth
          data-testid={`${baseTestId}-cancel`}
          variation={ButtonVariation.GHOST}
          onClick={onCancel}>
          Cancel
        </Button>
        <Button
          isAutoWidth
          data-testid={`${baseTestId}-confirm`}
          disabled={isConfirmDisabled}
          variation={ButtonVariation.SOLID}
          onClick={handleConfirm}>
          Remove User
        </Button>
      </div>
    </div>
  );
}

export default RemoveUserModal;
